// BK size guide (snippets/bk-fit-guide.liquid). The customer picks the EU size they usually wear on a horizontal
// ruler; the guide looks it up in the model's list (bk-size-map) and offers to select that size of this product.
// Only "Selectează" commits: it selects the size, remembers the usual size for this model on this device and turns the
// button of every product of the same model into "Recomandare: 43⅓". Until then the button keeps the advice (owner).
// The picker is re-rendered on every variant change, so everything is derived again in connectedCallback.
// v3: one usual size per model ("Yeezy 350 V2", "Nike Mind 001"), not one for every model (owner).
const STORAGE_KEY = 'bk-usual-size-v3';
// An unavailable recommendation offers the closest available size only within this many EU sizes (owner).
const MAX_ALTERNATIVE_GAP = 2.5;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

const parseSize = label => {
  const match = String(label).trim().match(/^(\d+(?:\.\d+)?)(?:\s+(\d)\/(\d))?$/);
  return match ? Number(match[1]) + (match[2] ? Number(match[2]) / Number(match[3]) : 0) : NaN;
};
const pretty = label => String(label).replace(' 1/3', '⅓').replace(' 2/3', '⅔');
const readAll = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; } };
const readUsual = model => readAll()[model] || null;
const saveUsual = (model, value) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [model]: value })); } catch {} };

class BkFitGuide extends HTMLElement {
  connectedCallback() {
    // Models within half a size show only the table.
    if (this.sizes || !this.querySelector('[data-ruler]')) return;
    // "usual:recommended" pairs from snippets/bk-size-map.liquid.
    this.map = new Map(this.dataset.map.split(';').map(pair => pair.split(':').map(part => part.trim())));
    this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
    this.sizes = JSON.parse(this.querySelector('[data-sizes]').textContent)
      .map(size => ({ ...size, value: parseSize(size.label) }))
      .filter(size => Number.isFinite(size.value))
      .sort((a, b) => a.value - b.value);
    this.modal = this.closest('bk-modal');
    this.track = this.querySelector('[data-track]');
    this.items = [...this.track.querySelectorAll('.bk-fit-ruler__item')];

    this.addEventListener('change', event => {
      if (!event.target.matches('.bk-fit-ruler__item input')) return;
      this.pick(event.target, true);
    });
    this.querySelector('[data-select]').addEventListener('click', event => this.select(event.currentTarget.dataset));
    // Scrolling the ruler previews the size under the lens and picks it once the ruler settles.
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.paint(true));
      clearTimeout(this.settle);
      if (!this.gliding) this.settle = setTimeout(() => this.settled(), 140);
    }, { passive: true });
    this.dragWithMouse();
    this.modal?.addEventListener('dialog:before-show', () => requestAnimationFrame(() => this.center(this.current(), false)));

    const usual = readUsual(this.dataset.model);
    const input = usual && this.track.querySelector(`input[value="${CSS.escape(usual)}"]`);
    if (input) {
      input.checked = true;
      this.updateTrigger(this.show(usual, false));
    }
  }

  current() {
    // Before the customer picks, the ruler rests on 40 (owner).
    return this.track.querySelector('input:checked') || this.track.querySelector('input[value="40"]');
  }

  center(input, smooth) {
    const item = input?.closest('.bk-fit-ruler__item');
    if (!item || !this.track.clientWidth) return;
    const left = item.offsetLeft - (this.track.clientWidth - item.offsetWidth) / 2;
    // Only a real scroll produces the scroll event that clears this flag.
    if (Math.abs(this.track.scrollLeft - left) > 1) {
      this.programmatic = true;
      this.track.scrollTo({ left, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'instant' });
    }
    this.paint();
  }

  nearest() {
    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    return this.items.reduce((best, item) => Math.abs(item.offsetLeft + item.offsetWidth / 2 - middle) < Math.abs(best.offsetLeft + best.offsetWidth / 2 - middle) ? item : best);
  }

  // Scale and fade the numbers by their distance from the lens, like a wheel. While the customer scrolls, the result
  // follows the size passing under the lens; it is saved only when the ruler settles.
  paint(live = false) {
    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    for (const item of this.items) {
      const distance = Math.min(Math.abs(item.offsetLeft + item.offsetWidth / 2 - middle) / item.offsetWidth, 3);
      item.style.setProperty('--bk-ruler-distance', distance.toFixed(2));
    }
    if (!live || this.programmatic) return;
    const passing = this.nearest();
    if (passing === this.passing) return;
    this.passing = passing;
    this.show(passing.querySelector('input').value, false, true);
  }

  // Settle on the nearest size and ease it under the lens (snapping is "proximity", so a fling can glide).
  settled() {
    if (this.programmatic) { this.programmatic = false; return; }
    const input = this.nearest().querySelector('input');
    if (!input.checked) input.checked = true;
    this.pick(input, true);
  }

  // Touch scrolls natively. With a mouse the ruler can be dragged and thrown; the glide is capped and slows down
  // quickly so a flick moves a few sizes, not the whole ruler.
  dragWithMouse() {
    const track = this.track;
    let startX = 0, startLeft = 0, pressed = false, moved = false, samples = [];
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimationFrame(this.glide);
      pressed = true; moved = false;
      startX = event.clientX; startLeft = track.scrollLeft; samples = [[event.timeStamp, event.clientX]];
    });
    track.addEventListener('pointermove', event => {
      if (!pressed) return;
      // Capture only once the mouse really moves, so a plain click still taps the size under it.
      if (!track.hasPointerCapture(event.pointerId)) {
        if (Math.abs(event.clientX - startX) <= 4) return;
        moved = true; this.gliding = true;
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - (event.clientX - startX);
      samples.push([event.timeStamp, event.clientX]);
      samples = samples.filter(([time]) => event.timeStamp - time < 90);
    });
    const release = event => {
      pressed = false;
      if (!track.hasPointerCapture(event.pointerId)) return;
      track.releasePointerCapture(event.pointerId);
      const [first] = samples, last = samples[samples.length - 1];
      let velocity = first && last[0] > first[0] ? (first[1] - last[1]) / (last[0] - first[0]) : 0;
      velocity = Math.max(-1.6, Math.min(1.6, velocity));
      let previous = performance.now();
      const step = now => {
        const elapsed = now - previous; previous = now;
        track.scrollLeft += velocity * elapsed;
        velocity *= Math.pow(0.9, elapsed / 16);
        if (Math.abs(velocity) > 0.03 && !reducedMotion.matches) { this.glide = requestAnimationFrame(step); return; }
        track.classList.remove('is-dragging');
        this.gliding = false;
        this.settled();
      };
      this.glide = requestAnimationFrame(step);
    };
    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    // A drag must not also count as a tap on the size under the pointer.
    track.addEventListener('click', event => { if (moved) { event.preventDefault(); event.stopPropagation(); moved = false; } }, true);
  }

  pick(input, recenter) {
    this.passing = input.closest('.bk-fit-ruler__item');
    this.show(input.value, true);
    if (recenter) this.center(input, true);
  }

  // The model's list gives the target; the product's first size at or above it is recommended. Never a size off this
  // product's size run (owner): past the largest size by less than one EU size, the largest size is recommended; one
  // size or more beyond either end, there is no recommendation and the guide says we don't have the customer's size.
  // The same when the run has a gap there (no 38.5 on a pair that jumps from 37.5 to 40): a full run never puts the
  // next size more than one EU size above the target.
  recommend(usual) {
    const target = parseSize(this.map.get(usual) || '');
    if (!Number.isFinite(target) || !this.sizes.length) return null;
    const smallest = this.sizes[0], largest = this.sizes[this.sizes.length - 1];
    if (target > largest.value + 0.01) return target - largest.value < 0.99 ? largest : null;
    if (smallest.value - target >= 0.99) return null;
    const next = this.sizes.find(size => size.value >= target - 0.01);
    return next.value - target > 1.01 ? null : next;
  }

  show(usual, animate, live = false) {
    const recommended = this.recommend(usual);
    const result = this.querySelector('[data-result]');
    const label = this.querySelector('.bk-fit-guide__result-label');
    const sizeText = this.querySelector('[data-result-size]');
    const note = this.querySelector('[data-result-note]');
    const button = this.querySelector('[data-select]');
    result.hidden = false;
    note.hidden = true;
    button.hidden = true;
    this.querySelectorAll('tr.is-recommended').forEach(row => row.classList.remove('is-recommended'));
    // No size of this model fits: the message replaces the recommended size.
    result.classList.toggle('is-empty', !recommended);
    label.hidden = sizeText.hidden = !recommended;
    if (!recommended) {
      sizeText.textContent = '';
      note.textContent = this.labels.none; note.hidden = false;
      return null;
    }
    if (sizeText.textContent !== pretty(recommended.label) && (animate || live) && !reducedMotion.matches) {
      sizeText.animate(live
        ? { transform: ['translateY(4px)', 'none'], opacity: [.55, 1] }
        : { transform: ['translateY(8px) scale(.9)', 'none'], opacity: [0, 1] },
        { duration: live ? 140 : 320, easing: 'cubic-bezier(.18,.86,.24,1.08)' });
    }
    sizeText.textContent = pretty(recommended.label);
    let selectable = recommended;
    if (!recommended.available) {
      const available = this.sizes.filter(size => size.available);
      selectable = available.sort((a, b) => Math.abs(a.value - recommended.value) - Math.abs(b.value - recommended.value) || b.value - a.value)[0] || null;
      // A size too far from the recommendation is no alternative: say only that the recommended size is not available.
      if (selectable && Math.abs(selectable.value - recommended.value) > MAX_ALTERNATIVE_GAP + 0.01) selectable = null;
      note.textContent = selectable
        ? this.labels.unavailable.replace('__SIZE__', pretty(recommended.label)).replace('__ALT__', pretty(selectable.label))
        : this.labels.unavailable_only.replace('__SIZE__', pretty(recommended.label));
      note.hidden = false;
    }
    if (selectable) {
      button.textContent = this.labels.select.replace('__SIZE__', pretty(selectable.label));
      button.dataset.size = selectable.label;
      button.dataset.valueId = selectable.id;
      button.hidden = false;
    }
    for (const row of this.querySelectorAll('tbody tr[data-size]')) {
      if (Math.abs(parseSize(row.dataset.size) - recommended.value) < 0.01) row.classList.add('is-recommended');
    }
    return recommended;
  }

  updateTrigger(recommended) {
    const trigger = this.modal && document.querySelector(`[data-bk-fit-trigger][aria-controls="${CSS.escape(this.modal.id)}"]`);
    const label = trigger?.querySelector('[data-bk-fit-label]');
    if (!label) return;
    label.dataset.default ??= label.innerHTML;
    trigger.classList.toggle('bk-fit-trigger--personal', !!recommended);
    if (!recommended) { label.innerHTML = label.dataset.default; return; }
    const caption = document.createElement('span');
    caption.className = 'bk-fit-trigger__caption';
    caption.textContent = this.labels.personal;
    const size = document.createElement('strong');
    size.textContent = pretty(recommended.label);
    label.replaceChildren(caption, ' ', size);
  }

  // Size inputs carry the option value ID; the label is a fallback for pickers that use names.
  // Close first: selecting re-renders the variant picker, which holds this dialog.
  select({ valueId, size }) {
    const usual = this.track.querySelector('input:checked')?.value;
    if (usual) {
      saveUsual(this.dataset.model, usual);
      this.updateTrigger(this.recommend(usual));
    }
    const picker = this.modal?.closest('variant-picker') || document.querySelector(`[aria-controls="${CSS.escape(this.modal.id)}"]`)?.closest('variant-picker');
    const inputs = picker ? [...picker.querySelectorAll('input[data-option-position]')] : [];
    const input = inputs.find(item => item.value === valueId) || inputs.find(item => item.value === size);
    if (input) this.modal.addEventListener('dialog:after-hide', () => { if (!input.checked) input.click(); }, { once: true });
    this.modal.hide();
  }
}

if (!customElements.get('bk-fit-guide')) customElements.define('bk-fit-guide', BkFitGuide);
